import { useState } from "react";
import { Alert, Button, Modal } from "react-bootstrap";

const priceListColumns = [
  {
    label: "Opis usługi",
    name: "name",
    type: "text",
    value: "",
  },
  {
    label: "Ilość",
    name: "quantity",
    type: "number",
    value: "",
  },
  {
    label: "Cena jednostkowa",
    name: "unitPrice",
    type: "number",
    value: "",
  },
  {
    label: "Wartość",
    name: "value",
    type: "sum",
    value: "",
  },
];

function parsePriceNumber(value) {
  const normalizedValue = String(value).replace(/\s/g, "");
  const numberText = normalizedValue.match(/[\d.,]+/)?.[0];

  if (!numberText) return 0;

  const decimalSeparator = Math.max(numberText.lastIndexOf(","), numberText.lastIndexOf("."));
  if (decimalSeparator === -1) return Number(numberText);

  const integerPart = numberText.slice(0, decimalSeparator).replace(/[,.]/g, "");
  const decimalPart = numberText.slice(decimalSeparator + 1);

  return Number(`${integerPart}.${decimalPart}`);
}

function formatPrice(value) {
  return `${Number(value).toFixed(2)} zł`;
}

function createPriceListRow(name, quantity, unitPrice) {
  const rowData = {
    name,
    quantity,
    unitPrice,
    value: formatPrice(Number(quantity) * Number(unitPrice)),
  };

  return priceListColumns.map((column) => ({
    ...column,
    value: rowData[column.name],
  }));
}

function createPriceListRowFromValue(name, quantity, value) {
  const quantityNumber = parsePriceNumber(quantity);
  const valueNumber = parsePriceNumber(value);
  const rowData = {
    name,
    quantity: quantityNumber,
    unitPrice: valueNumber / quantityNumber,
    value: formatPrice(valueNumber),
  };

  return priceListColumns.map((column) => ({
    ...column,
    value: rowData[column.name],
  }));
}

function createPriceListTable(value) {
  return {
    name: "table_price_list",
    label: "",
    value,
    type: "table_price_list",
    columns: priceListColumns.map((column) => ({ ...column })),
    totalMultiplier: 1.0,
    total: formatPrice(
      value.reduce((total, row) => {
        return total + parsePriceNumber(row[3].value);
      }, 0)
    ),
  };
}

export function migrateTableOfferToPriceList(table) {
  return createPriceListTable(
    table.value.map((row) =>
      createPriceListRow(
        row[0].value,
        parsePriceNumber(row[1].value),
        parsePriceNumber(row[2].value)
      )
    )
  );
}

export function migrateTableAssortmentsToPriceList(table) {
  return createPriceListTable(
    table.value.map((row) =>
      createPriceListRow(
        row[0].value,
        1,
        parsePriceNumber(row[1].value)
      )
    )
  );
}

export function migrateTableToPriceList(table) {
  return createPriceListTable(
    table.value.map((row) =>
      createPriceListRowFromValue(
        row[0].value,
        row[1].value,
        row[2].value
      )
    )
  );
}

function migrateOfferTablesToPriceList(elements) {
  let migratedTablesCount = 0;

  const migratedElements = elements.map((element) => {
    if (element.type === "table_offer") {
      migratedTablesCount++;
      return migrateTableOfferToPriceList(element);
    }

    if (element.name === "table_assortments") {
      migratedTablesCount++;
      return migrateTableAssortmentsToPriceList(element);
    }

    if (element.type === "table") {
      migratedTablesCount++;
      return migrateTableToPriceList(element);
    }

    if (element.children && Array.isArray(element.children)) {
      const migratedChildren = migrateOfferTablesToPriceList(element.children);
      migratedTablesCount += migratedChildren.migratedTablesCount;
      return {
        ...element,
        children: migratedChildren.elements,
      };
    }

    return element;
  });

  return {
    elements: migratedElements,
    migratedTablesCount,
  };
}

export { migrateOfferTablesToPriceList };

export default function OfferMigrationDialog({ show, onHide, elements, onMigrate }) {
  const [error, setError] = useState("");

  const handleMigrate = () => {
    const migrationResult = migrateOfferTablesToPriceList(elements);

    if (migrationResult.migratedTablesCount === 0) {
      setError("Nie znaleziono tabeli do migracji.");
      return;
    }

    setError("");
    onMigrate(migrationResult.elements);
  };

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Migracja oferty</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error ? <Alert variant="danger">{error}</Alert> : null}
        Ofertę można migrować do nowszej wersji z tabelą cen.
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Anuluj
        </Button>
        <Button variant="primary" onClick={handleMigrate}>
          Migruj
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
